import React from "react";
import Icon from "./Icon";
import { LANGS } from "../../../i18n";
import { t } from "@lingui/core/macro";

/**
 * Sélecteur de langue pour le thème Travel.
 * Utilise le même mécanisme que LangSelect de i18n (redirection URL)
 * mais avec le design du thème Travel.
 */
export default function LangSwitcher({ context }) {
    const { org, lang } = context;
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef(null);

    React.useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    if (!Array.isArray(org.languages) || org.languages.length === 0) {
        return null;
    }

    const defaultLanguage = org.language || "fr";
    const availableLangs = LANGS.filter(i => org.languages.includes(i.value));
    const currentLang = LANGS.find(i => i.value === (lang?.value || defaultLanguage)) || LANGS.find(i => i.value === "fr");

    const onChange = (key) => {
        const base = org.BASE_WEB_URL.split("/");
        const route = context.route;
        let query = [];
        for (let q of Object.keys(context.query).filter(i => i !== "router")) {
            query.push(q + "=" + context.query[q]);
        }

        const lastBase = base[base.length - 1];
        if (LANGS.find(i => i.value === lastBase)) {
            base[base.length - 1] = key;
        } else {
            base.push(key);
        }

        let url = base.concat(route).join("/");
        if (query.length > 0) {
            url += "?" + query.join("&");
        }
        window.location.href = url;
    };

    return (
        <div className="tr-lang" ref={rootRef}>
            <button
                className="tr-lang__trigger tr-focusable"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={t`Langue`}
                onClick={() => setOpen(v => !v)}
            >
                <span
                    className={`fi fi-${String(currentLang?.flag || "").toLowerCase()} tr-lang__flag`}
                    style={{
                        width: 26,
                        height: 20,
                        borderRadius: 5,
                        backgroundSize: "cover",
                        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.12)",
                    }}
                    role="img"
                    aria-label={currentLang?.formatLabel}
                />
                <Icon name="chevronDown" size={14} />
            </button>

            {open && (
                <div className="tr-lang__menu" role="menu">
                    {[LANGS.find(i => i.value === defaultLanguage), ...availableLangs]
                        .filter(Boolean)
                        .filter((l, idx, arr) => arr.findIndex(x => x.value === l.value) === idx)
                        .map(l => (
                            <button
                                key={l.value}
                                role="menuitem"
                                className="tr-lang__item tr-focusable"
                                aria-current={l.value === (lang?.value || defaultLanguage)}
                                onClick={() => { onChange(l.value); setOpen(false); }}
                            >
                                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span
                                        className={`fi fi-${String(l.flag || "").toLowerCase()} tr-lang__flag`}
                                        style={{
                                            width: 26,
                                            height: 20,
                                            borderRadius: 5,
                                            backgroundSize: "cover",
                                            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.12)",
                                        }}
                                        role="img"
                                        aria-label={l.formatLabel}
                                    />
                                    {l.formatLabel}
                                </span>
                                <span className="tr-lang__short">{l.value.toUpperCase()}</span>
                            </button>
                        ))}
                </div>
            )}
        </div>
    );
}
